import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash, FaBuilding, FaUser, FaPercent, FaEuroSign, FaExclamationTriangle, FaHospital } from 'react-icons/fa';

const SplitConfiguration = ({ 
  activity, 
  onSplitChange, 
  availablePayers = [],
  availablePatients = [],      // 🆕 NUEVO
  availableInsurers = [],       // 🆕 NUEVO
  availableHealthCenters = [],  // 🆕 NUEVO
  initialSplits = null,         // 🆕 NUEVO - Para edición
  className = "" 
}) => {
  // 🔧 INICIALIZAR CON SPLITS EXISTENTES O CREAR UNO NUEVO DEL PACIENTE
  const [splits, setSplits] = useState(() => {
    if (initialSplits && initialSplits.length > 0) {
      return initialSplits;
    }
    
    // Split por defecto del paciente
    return [{
      id: Date.now(),
      payer_id: activity.patient_id,
      payer_type: 'User',
      percent: 100,
      max_amount: null,
      payer_snapshot: {
        name: activity.patient_name,
        display_name: activity.patient_name,
        type_description: 'Paciente',
        contact: {
          phone: activity.patient_phone || '',
          email: activity.patient_email || ''
        },
        address: activity.patient_address || {}
      }
    }];
  });
  
  const [totalPercent, setTotalPercent] = useState(100);
  const [isValid, setIsValid] = useState(true);
  const [validationErrors, setValidationErrors] = useState([]);
  
  // 🔧 CALCULAR TOTAL Y VALIDAR
  useEffect(() => {
    const total = splits.reduce((sum, split) => sum + (parseFloat(split.percent) || 0), 0);
    const errors = [];
    
    // Validar porcentajes
    const percentValid = Math.abs(total - 100) <= 0.01;
    if (!percentValid) {
      errors.push(`Los porcentajes suman ${total.toFixed(2)}% (debe ser 100%)`);
    }
    
    // Validar que todos los splits tienen payer_id válido
    const emptySplits = splits.filter(split => !split.payer_id || split.payer_id.trim() === '');
    if (emptySplits.length > 0) {
      errors.push(`${emptySplits.length} pagador(es) sin seleccionar`);
    }
    
    // Validar que no hay porcentajes negativos o > 100
    const invalidPercents = splits.filter(split => split.percent < 0 || split.percent > 100);
    if (invalidPercents.length > 0) {
      errors.push(`Porcentajes inválidos detectados`);
    }
    
    // 🔧 VALIDAR QUE CADA SPLIT TIENE PAYER_SNAPSHOT
    const missingSplits = splits.filter(split => !split.payer_snapshot || !split.payer_snapshot.name);
    if (missingSplits.length > 0) {
      errors.push(`${missingSplits.length} pagador(es) sin datos completos`);
    }
    
    const valid = errors.length === 0;
    
    setTotalPercent(total);
    setValidationErrors(errors);
    setIsValid(valid);
    
    // Notificar cambios al padre
    if (onSplitChange && typeof onSplitChange === 'function') {
      const splitsCopy = splits.map(split => ({ ...split }));
      
      console.log(`✅ SplitConfiguration: Notificando cambios`, {
        key: activity.key,
        splits_count: splitsCopy.length,
        isValid: valid,
        errors: errors.length
      });
      
      onSplitChange(activity.key, splitsCopy, valid);
    }
  }, [splits, activity.key]);

  // 🆕 FUNCIÓN PARA OBTENER PAYER_SNAPSHOT COMPLETO
  const buildPayerSnapshot = (payerId, payerType) => {
    let payer = null;
    
    switch (payerType) {
      case 'User':
        payer = availablePatients.find(p => p._id === payerId);
        if (payer) {
          return {
            name: payer.fullName || `${payer.name} ${payer.surname}`,
            display_name: payer.display_name || payer.fullName,
            type_description: 'Paciente',
            contact: {
              phone: payer.phone || '',
              email: payer.email || ''
            },
            address: {
              street: payer.address || '',
              city: payer.city || '',
              state: payer.state || '',
              zipcode: payer.zipcode || ''
            }
          };
        }
        break;
        
      case 'Company':
        payer = availableInsurers.find(c => c._id === payerId);
        if (payer) {
          return {
            name: payer.name,
            display_name: payer.display_name || payer.name,
            type_description: 'Aseguradora',
            cif: payer.cif || '',
            contact: {
              phone: payer.phone || '',
              email: payer.email || '',
              website: payer.website || ''
            },
            address: {
              street: payer.address || '',
              city: payer.city || '',
              state: payer.state || '',
              zipcode: payer.zipcode || ''
            },
            center_info: {
              insurance_contracts: [],
              insurance_types: payer.medicalExtension?.insurance || [],
              specialties: payer.medicalExtension?.specialties || []
            }
          };
        }
        break;
        
      case 'HealthCenter':
        payer = availableHealthCenters.find(h => h._id === payerId);
        if (payer) {
          return {
            name: payer.name,
            display_name: payer.display_name || payer.name,
            type_description: 'Centro Médico',
            cif: payer.cif || '',
            contact: {
              phone: payer.phone || '',
              email: payer.email || '',
              website: payer.website || ''
            },
            address: {
              street: payer.address || '',
              city: payer.city || '',
              state: payer.state || '',
              zipcode: payer.zipcode || ''
            },
            center_info: {
              insurance_contracts: [],
              services: payer.services || [],
              specialties: payer.specialties || [],
              departments: payer.departments || []
            }
          };
        }
        break;
    }
    
    return null;
  };

  // 🆕 MANEJAR CAMBIO DE TIPO DE PAGADOR
const handlePayerTypeChange = (index, newPayerType) => {
  console.log(`📋 Cambiando tipo de pagador del split ${index} a:`, newPayerType);
  
  // Obtener pagadores según el tipo
  const payers = getPayerOptions(newPayerType);
  console.log(`👥 Pagadores disponibles (${newPayerType}):`, payers.length);
  
  // Actualizar el split limpiando payer_id y snapshot
  updateSplit(index, 'payer_type', newPayerType);
};

// 🆕 MANEJAR SELECCIÓN DE PAGADOR ESPECÍFICO
const handlePayerSelection = (index, payerId) => {
  console.log(`✅ Pagador seleccionado para split ${index}:`, payerId);
  
  if (!payerId) {
    updateSplit(index, 'payer_id', '');
    updateSplit(index, 'payer_snapshot', null);
    return;
  }
  
  const currentPayerType = splits[index].payer_type;
  console.log(`🔍 Buscando pagador con tipo:`, currentPayerType);
  
  // Obtener las opciones según el tipo
  const payerOptions = getPayerOptions(currentPayerType);
  const selectedPayer = payerOptions.find(p => p._id === payerId);
  
  if (!selectedPayer) {
    console.error('❌ Pagador no encontrado:', payerId);
    return;
  }
  
  console.log('📦 Datos del pagador encontrado:', selectedPayer);
  
  // Construir el payer_snapshot completo
  const payerSnapshot = buildPayerSnapshot(payerId, currentPayerType);
  
  console.log('✅ Payer Snapshot construido:', payerSnapshot);
  
  // Actualizar el split con el ID y el snapshot
  const newSplits = [...splits];
  newSplits[index] = {
    ...newSplits[index],
    payer_id: payerId,
    payer_snapshot: payerSnapshot
  };
  
  setSplits(newSplits);
};

  // 🔧 AÑADIR NUEVO SPLIT
  const addSplit = () => {
    console.log('➕ Añadiendo nuevo split');
    
    const newSplits = [...splits];
    
    // Redistribuir porcentajes
    if (totalPercent >= 100 && newSplits.length > 0) {
      const reductionPerSplit = 100 / (newSplits.length + 1);
      
      newSplits.forEach((split, index) => {
        newSplits[index] = { ...split, percent: Math.round(reductionPerSplit * 100) / 100 };
      });
    }
    
    const totalAfterReduction = newSplits.reduce((sum, s) => sum + s.percent, 0);
    const remainingPercent = Math.max(0, 100 - totalAfterReduction);
    
    const newSplit = {
      id: Date.now(),
      payer_id: '',
      payer_type: 'Company',
      percent: remainingPercent > 0 ? Math.round(remainingPercent * 100) / 100 : Math.round((100 / (newSplits.length + 1)) * 100) / 100,
      max_amount: null,
      payer_snapshot: null // Se llenará cuando se seleccione el pagador
    };
    
    setSplits([...newSplits, newSplit]);
  };

  // 🔧 ACTUALIZAR SPLIT
  const updateSplit = (index, field, value) => {
    console.log(`🔄 Actualizando split ${index}:`, { field, value });
    
    const newSplits = [...splits];
    newSplits[index] = { ...newSplits[index], [field]: value };
    
    // Si se cambia el tipo de pagador, limpiar el payer_id y snapshot
    if (field === 'payer_type') {
      newSplits[index].payer_id = '';
      newSplits[index].payer_snapshot = null;
    }
    
    // Si se cambia el payer_id, actualizar el snapshot
    if (field === 'payer_id' && value) {
      const snapshot = buildPayerSnapshot(value, newSplits[index].payer_type);
      newSplits[index].payer_snapshot = snapshot;
      
      console.log('📦 Payer snapshot actualizado:', snapshot);
    }
    
    setSplits(newSplits);
  };

  // 🔧 ACTUALIZAR PORCENTAJE
  const handlePercentChange = (index, value) => {
    const percent = Math.max(0, Math.min(100, parseFloat(value) || 0));
    updateSplit(index, 'percent', percent);
  };

  // 🔧 REMOVER SPLIT
  const removeSplit = (index) => {
    if (splits.length > 1) {
      const splitToRemove = splits[index];
      
      // No permitir eliminar el split del paciente si es el único de tipo User
      const userSplits = splits.filter(s => s.payer_type === 'User');
      if (splitToRemove.payer_type === 'User' && userSplits.length === 1) {
        alert('Debe mantener al menos un split del paciente');
        return;
      }
      
      setSplits(splits.filter((_, i) => i !== index));
    }
  };

  // 🔧 DISTRIBUIR UNIFORMEMENTE
  const distributeEvenly = () => {
    const percentPerSplit = Math.round((100 / splits.length) * 100) / 100;
    let remainder = 100 - (percentPerSplit * splits.length);
    
    const newSplits = splits.map((split, index) => ({
      ...split,
      percent: index === 0 ? percentPerSplit + remainder : percentPerSplit
    }));
    
    setSplits(newSplits);
  };

  // 🆕 OBTENER OPCIONES DE PAGADOR SEGÚN TIPO
  const getPayerOptions = (payerType) => {
    switch (payerType) {
      case 'User':
        return availablePatients;
      case 'Company':
        return availableInsurers;
      case 'HealthCenter':
        return availableHealthCenters;
      default:
        return [];
    }
  };

  // 🔧 OBTENER NOMBRE DEL PAGADOR
  const getPayerName = (payerId, payerType) => {
    if (!payerId) return 'Seleccionar...';
    
    const options = getPayerOptions(payerType);
    const payer = options.find(p => p._id === payerId);
    
    if (payer) {
      return payer.fullName || payer.display_name || payer.name;
    }
    
    if (payerId === activity.patient_id) {
      return activity.patient_name || 'Paciente';
    }
    
    return 'Desconocido';
  };

  // CALCULAR IMPORTE
  const calculateAmount = (percent) => {
    const total = activity.total_amount || 0;
    return (total * percent / 100).toFixed(2);
  };

  // VALIDAR SPLIT INDIVIDUAL
  const validateSplit = (split) => {
    const errors = [];
    
    if (!split.payer_id || split.payer_id.trim() === '') {
      errors.push('Debe seleccionar un pagador');
    }
    
    if (split.percent <= 0) {
      errors.push('El porcentaje debe ser mayor a 0');
    }
    
    if (split.percent > 100) {
      errors.push('El porcentaje no puede exceder 100%');
    }
    
    if (!split.payer_snapshot || !split.payer_snapshot.name) {
      errors.push('Faltan datos del pagador');
    }
    
    return errors;
  };

  // 🆕 OBTENER ICONO SEGÚN TIPO
  const getPayerIcon = (payerType) => {
    switch (payerType) {
      case 'User':
        return <FaUser className="me-1" />;
      case 'Company':
        return <FaBuilding className="me-1" />;
      case 'HealthCenter':
        return <FaHospital className="me-1" />;
      default:
        return null;
    }
  };

  return (
    <div className={`split-configuration ${className}`}>
      <div className="card-header d-flex justify-content-between align-items-center">
        <div>
          <h6 className="mb-1">
            <FaPercent className="me-2" />
            Configuración de Pagadores
          </h6>
          <small className="text-muted">
            {activity?.description} - Total: €{activity?.total_amount?.toFixed(2) || '0.00'}
          </small>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={distributeEvenly}
            title="Distribuir uniformemente"
            disabled={splits.length === 0}
          >
            Distribuir Uniformemente
          </button>
          <button
            className="btn btn-sm btn-primary"
            onClick={addSplit}
            title="Agregar Pagador"
            disabled={splits.length >= 5}
          >
            <FaPlus className="me-1" />
            Agregar Pagador
          </button>
        </div>
      </div>

      {/* Lista de splits */}
      <div className="splits-list">
        {splits.map((split, index) => {
          const splitErrors = validateSplit(split);
          const isPatientSplit = split.payer_id === activity.patient_id && split.payer_type === 'User';
          const hasErrors = splitErrors.length > 0;
          const payerOptions = getPayerOptions(split.payer_type);
          
          return (
            <div key={split.id || index} className={`card mb-2 ${hasErrors ? 'border-warning' : ''}`}>
              <div className="card-body p-3">
                <div className="row g-2 align-items-center">
                  {/* Tipo de pagador */}
                  <div className="col-md-3">
                    <label className="form-label small">Tipo</label>
                    <select
                      className="form-select form-select-sm"
                      value={split.payer_type}
                      onChange={(e) => handlePayerTypeChange(index, e.target.value)}
                      disabled={isPatientSplit}
                    >
                      <option value="User">
                        {getPayerIcon('User')} Paciente
                      </option>
                      <option value="Company">
                        {getPayerIcon('Company')} Aseguradora/Empresa
                      </option>
                      <option value="HealthCenter">
                        {getPayerIcon('HealthCenter')} Centro Médico
                      </option>
                    </select>
                  </div>

                  {/* Selección de pagador */}
                  <div className="col-md-3">
                    <label className="form-label small">Pagador</label>
                    {isPatientSplit ? (
                      <input
                        type="text"
                        className="form-control form-control-sm bg-light"
                        value={activity.patient_name || 'Paciente'}
                        readOnly
                      />
                    ) : (
                      <select
  className={`form-select form-select-sm ${!split.payer_id ? 'border-warning' : ''}`}
  value={split.payer_id}
  onChange={(e) => handlePayerSelection(index, e.target.value)}
>
                        <option value="">-- Seleccionar pagador --</option>
                        {payerOptions.map(payer => (
                          <option key={payer._id} value={payer._id}>
                            {payer.fullName || payer.display_name || payer.name}
                            {payer.cif && ` (CIF: ${payer.cif})`}
                          </option>
                        ))}
                      </select>
                    )}
                    
                    {/* Mostrar errores */}
                    {splitErrors.length > 0 && (
                      <div className="text-warning small mt-1">
                        {splitErrors.map((error, i) => (
                          <div key={i}>
                            <FaExclamationTriangle className="me-1" />
                            {error}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* 🆕 Mostrar info del payer_snapshot si existe */}
                    {split.payer_snapshot && (
                      <div className="text-muted small mt-1">
                        ✓ Datos completos cargados
                      </div>
                    )}
                  </div>

                  {/* Porcentaje */}
                  <div className="col-md-2">
                    <label className="form-label small">% Copago</label>
                    <div className="input-group input-group-sm">
                      <input
                        type="number"
                        className={`form-control ${split.percent <= 0 || split.percent > 100 ? 'border-warning' : ''}`}
                        value={split.percent}
                        onChange={(e) => handlePercentChange(index, e.target.value)}
                        min="0"
                        max="100"
                        step="0.01"
                      />
                      <span className="input-group-text">%</span>
                    </div>
                  </div>

                  {/* Importe calculado */}
                  <div className="col-md-2">
                    <label className="form-label small">Importe</label>
                    <div className="form-control form-control-sm bg-light text-end">
                      €{calculateAmount(split.percent)}
                    </div>
                  </div>

                  {/* Tope máximo */}
                  <div className="col-md-1">
                    <label className="form-label small">Tope €</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={split.max_amount || ''}
                      onChange={(e) => updateSplit(index, 'max_amount', e.target.value ? parseFloat(e.target.value) : null)}
                      min="0"
                      step="0.01"
                      placeholder="Sin tope"
                    />
                  </div>

                  {/* Acciones */}
                  <div className="col-md-1">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeSplit(index)}
                      disabled={splits.length <= 1 || isPatientSplit}
                      title={isPatientSplit ? 'No se puede eliminar el split del paciente' : 'Eliminar pagador'}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Resumen */}
      <div className="validation-summary">
        <div className={`card ${isValid ? 'border-success' : 'border-danger'}`}>
          <div className="card-body p-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong className={isValid ? 'text-success' : 'text-danger'}>
                  Total porcentajes: {totalPercent.toFixed(2)}%
                </strong>
                
                {validationErrors.length > 0 && (
                  <div className="text-danger small mt-1">
                    {validationErrors.map((error, index) => (
                      <div key={index}>
                        <FaExclamationTriangle className="me-1" />
                        {error}
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="small mt-1">
                  {isValid ? (
                    <span className="text-success">✓ Configuración válida</span>
                  ) : (
                    <span className="text-danger">✗ Configuración incompleta</span>
                  )}
                </div>
              </div>
              
              <div className="text-end">
                <div className="text-muted small">Total a facturar:</div>
                <strong className="text-success">
                  €{activity?.total_amount?.toFixed(2) || '0.00'}
                </strong>
                
                {isValid && (
                  <div className="mt-1">
                    {splits.filter(split => split.payer_id).map((split, idx) => (
                      <div key={split.id || idx} className="text-xs text-muted">
                        {getPayerName(split.payer_id, split.payer_type)}: 
                        €{calculateAmount(split.percent)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SplitConfiguration;